import React, {
  useContext,
  useEffect,
  useMemo,
  useCallback,
  useReducer,
} from 'react';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import PropTypes from 'prop-types';

import useSessionStore from '../../hooks/useSessionStore';
import UserContext from '../UserContext';
import InternalAssignment from '../../Model/InternalAssignment';
import CoachPod from '../../Model/CoachPod';
import User from '../../Model/User';
import Coach from '../../Model/Coach';

import PodContext from './PodContext';

const initialPodData = {
  selectedPodId: null,
  pods: [],
  users: null,
  isReady: false,
  assistantCoaches: [],
  coachInternalAssignments: [],
};

function podDataReducer(state, action) {
  switch (action.type) {
    case 'SET_INITIAL_DATA':
      return action.payload;
    case 'SET_POD_LOADING':
      return { ...state, isReady: false };
    case 'SET_USERS_AND_SELECTED_POD_ID':
      return {
        ...state, users: action.users, selectedPodId: action.selectedPodId, isReady: true,
      };
    case 'SET_PODS':
      return { ...state, pods: action.pods };
    case 'SET_ASSISTANT_COACHES_AND_ASSIGNMENTS':
      return {
        ...state,
        assistantCoaches: action.assistantCoaches,
        coachInternalAssignments: action.coachInternalAssignments,
      };
    default:
      return state;
  }
}

const PodContextProvider = ({ children }) => {
  const { authUser: { uid: authUserId } = {} } = useSessionStore();
  const { userId: userInRouteId } = useContext(UserContext);

  const [podData, dispatch] = useReducer(podDataReducer, initialPodData);

  const init = useCallback(async () => {
    let selectedPodId = null;
    let pods = [];
    let users = null;
    let isReady = false;

    const assignments = await InternalAssignment.getAllCurrentAssignments(authUserId, 'AC', userInRouteId);
    if (assignments.hasDocs) {
      selectedPodId = assignments.docs[0].data.podId;
      if (selectedPodId) {
        const usersRef = await User.getUserByPodId(selectedPodId);
        if (usersRef.hasDocs) {
          users = usersRef.docs.map((doc) => doc.id);
        }
      }
    }

    const podSnap = await CoachPod.getPodsByCoach(userInRouteId);

    if (podSnap.hasDocs) {
      pods = podSnap.docs;
    }

    isReady = true;

    dispatch({
      type: 'SET_INITIAL_DATA',
      payload: {
        selectedPodId,
        pods,
        users,
        isReady,
      },
    });
  }, [authUserId, userInRouteId]);

  useEffect(() => {
    init();
  }, [init]);

  const updateSelectedPodId = useCallback(async (newPodId) => {
    dispatch({ type: 'SET_POD_LOADING' });
    let users = null;
    if (newPodId) {
      const usersRef = await User.getUserByPodId(newPodId);
      if (usersRef.hasDocs) {
        users = usersRef.docs.map((doc) => doc.id);
      }
    }
    dispatch({ type: 'SET_USERS_AND_SELECTED_POD_ID', users, selectedPodId: newPodId });
  }, []);

  const fetchPodsForCoach = useCallback(async (coachId) => {
    let pods = [];
    if (coachId) {
      const podsRef = await CoachPod.getPodsByCoach(coachId);
      if (podsRef.hasDocs) {
        pods = podsRef.docs;
      }
    }
    dispatch({ type: 'SET_PODS', pods });
  }, []);

  const setAssistantCoachesAndAssignment = useCallback(async (coachId) => {
    let assistantCoaches = [];
    let coachInternalAssignments = [];

    if (coachId) {
      const [assistantCoachSnap, internalAssignmentSnap] = await Promise.all([
        Coach.getAssistantCoaches(),
        InternalAssignment.getAllCurrentAssignments(null, 'AC', coachId),
      ]);

      if (internalAssignmentSnap.hasDocs) {
        coachInternalAssignments = internalAssignmentSnap.docs;
      }

      if (internalAssignmentSnap.hasDocs && assistantCoachSnap.hasDocs) {
        const filteredAssistantCoaches = assistantCoachSnap.docs.filter(
          (doc) => internalAssignmentSnap.docs.some((assignment) => assignment.data.userId === doc.id),
        );
        assistantCoaches = filteredAssistantCoaches;
      }

      dispatch({ type: 'SET_ASSISTANT_COACHES_AND_ASSIGNMENTS', coachInternalAssignments, assistantCoaches });
    }
  }, []);

  const context = useMemo(() => ({
    selectedPodId: podData.selectedPodId,
    pods: podData.pods,
    users: podData.users,
    isReady: podData.isReady,
    assistantCoaches: podData.assistantCoaches,
    coachInternalAssignments: podData.coachInternalAssignments,
    updateSelectedPodId,
    fetchPodsForCoach,
    setAssistantCoachesAndAssignment,
  }), [
    podData.selectedPodId,
    podData.pods,
    podData.users,
    podData.isReady,
    podData.assistantCoaches,
    podData.coachInternalAssignments,
    updateSelectedPodId,
    fetchPodsForCoach,
    setAssistantCoachesAndAssignment,
  ]);

  return (
    <PodContext.Provider value={context}>
      {children}
    </PodContext.Provider>
  );
};

PodContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default compose(
  observer,
)(PodContextProvider);
